import type {FieldCode, FieldList, FieldSelector, MultiFieldSelector, Rec, SubfieldedFieldSelector, ViewableField, ViewableFieldWithError, WholeFieldSelector} from 'src/typings';
import {exists, isNullOrUndefined} from './custom-utils';
import {isObject} from 'shared/utils/types-utils';
import {isString} from 'shared/utils/string-utils';

export const WELL_KNOWN_FIELDS = {
    documentTitle: {
        code: 'd245',
        responsibilityStatement: {
            code: 'c'
        }
    },

    mainAuthor: {
        code: 'd100',
        a: {
            code: 'a'
        }
    },

    documentPublicationOld: {
        code: 'd260',
        place: {
            code: 'a'
        },
        publisher: {
            code: 'b'
        },
        year: {
            code: 'c'
        }
    },

    documentPublicationRda: {
        code: 'd264',
        place: {
            code: 'a'
        },
        publisher: {
            code: 'b'
        },
        year: {
            code: 'c'
        }
    },

    otherAuthor: {
        code: 'd700',
        a: {
            code: 'a'
        }
    },

    sourceDocument: {
        code: 'd773',
        refholder: {
            code: 'main',
            t: {
                code: 't'
            },
            d: {
                code: 'd'
            },
            h: {
                code: 'h'
            },
            x: {
                code: 'x'
            },
            s: {
                code: 's'
            }
        },
        g: {
            code: 'g'
        },
        f: {
            code: 'f'
        },
        v: {
            code: 'v'
        },
    },

    series: {
        code: 'd787',
        zCykluInfix: 'z cyklu',
        g: {
            code: 'g'
        }
    },

    locationStatements: {
        code: 'd910',
        signature: {
            code: 'b'
        }
    },
};


/**
 * Method extracts fields described by numbers in input argument from record
 * If record doesn't contain any field, it returns [] - empty array
 *
 * @param {Rec} fieldContainer
 * @param {string | string[]} fieldNumbers Number of fields which should be extracted
 * @returns {Field[]} Extracted fields
 */
export function getFields(fieldContainer: FieldList, fieldNumbers: string | string[]): ViewableField[] {
    if (isNullOrUndefined(fieldNumbers)) {
        fieldNumbers = [];
    } else if (!Array.isArray(fieldNumbers)) {
        fieldNumbers = [fieldNumbers];
    }
    if (!fieldContainer.fields) {
        return [];
    }
    return fieldContainer.fields.filter((field) => fieldNumbers.includes(field.code));
}

/**
 * Method extracts sub-fields described by fields number and sub-fields identification from record
 * method return all subfields of found fields if no subfield code is provided
 * If record doesn't contain any field, it returns [] - empty array
 *
 * @param {Document} record
 * @param {string | string[]} fieldNumbers Number of fields which should be extracted
 * @param {string=} codes Sub-field codes
 * @returns {Field[]} extracted sub-fields
 */
export function getSubfields(record: Rec, fieldNumbers: string | string[], codes?: string[]): ViewableField[] {
    const fields = getFields(record, fieldNumbers);
    const subfields = getSubfieldsOfFields(fields);

    if (isNullOrUndefined(codes)) {
        return subfields
    }
    return subfields.filter((subfield) => codes.includes(subfield.code));
}

export function getFieldsRecursively(fieldContainer: FieldList, fieldSelector: FieldSelector): ViewableField[] {
    const multiFieldSelector: MultiFieldSelector = Array.isArray(fieldSelector) ? fieldSelector : [fieldSelector];

    return multiFieldSelector.flatMap((singleFieldSel) => {
        if (isObject(singleFieldSel)) {
            const subfieldedFieldSel: SubfieldedFieldSelector = singleFieldSel;
            const code: FieldCode = Object.keys(subfieldedFieldSel)[0];
            const subfieldSelector: FieldSelector = Object.values(subfieldedFieldSel)[0];

            const subfields = getFields(fieldContainer, code);
            return subfields.flatMap((subfield) => getFieldsRecursively(subfield, subfieldSelector));
        }

        if (isString(singleFieldSel)) {
            const code: WholeFieldSelector = singleFieldSel;
            return getFields(fieldContainer, code);
        }

        throw new Error(`Unknown field selector type ${typeof singleFieldSel}`);
    });
}

export function isFieldWithError(field: ViewableField): field is ViewableFieldWithError {
    return 'error' in field && exists(field.error);
}

function getSubfieldsOfFields(fields: ViewableField[]): ViewableField[] {
    return fields.flatMap((field) => field.fields ?? []);
}
